import * as React from "react";
import * as RadioGroupPrimitive from "@radix-ui/react-radio-group";
import { Slot } from "@radix-ui/react-slot";

import { cn } from "../../lib/utils";
import {
  segmentedSegmentBaseClassName,
  segmentedSelectedClassName,
  segmentedTrackSurfaceClassName,
} from "./segmented-tabs-styles";

export type SegmentedControlSize = "sm" | "md";

/**
 * How a `SegmentedControl` announces and applies its choice.
 *
 * - `radio` (default): a radiogroup. Arrow keys move between options and
 *   select as they go; Tab leaves the group.
 * - `pressed`: a labelled group of toggle buttons with `aria-pressed`. Each
 *   option is its own Tab stop and nothing changes until one is pressed. Use
 *   it when a change can be refused or reloads a heavy view, so walking the
 *   options with the arrow keys must not apply each one.
 */
export type SegmentedControlSelection = "radio" | "pressed";

type SegmentedContextValue = {
  size: SegmentedControlSize;
  fullWidth: boolean;
  kind: SegmentedControlSelection | "nav";
  value?: string;
  onSelect?: (value: string) => void;
  disabled?: boolean;
};

const SegmentedContext = React.createContext<SegmentedContextValue | null>(
  null,
);

function useSegmentedContext(component: string): SegmentedContextValue {
  const context = React.useContext(SegmentedContext);
  if (!context) {
    throw new Error(
      `${component} must be rendered inside SegmentedControl or SegmentedNav`,
    );
  }
  return context;
}

const TRACK_SIZE: Record<SegmentedControlSize, string> = {
  sm: "gap-0.5 p-0.5",
  md: "gap-1 p-1",
};

const SEGMENT_SIZE: Record<SegmentedControlSize, string> = {
  sm: "h-7 gap-1.5 px-2.5 text-xs [&_svg]:size-3.5 [&_svg]:shrink-0",
  md: "h-8 gap-2 px-3 text-sm [&_svg]:size-4 [&_svg]:shrink-0",
};

function trackClassName(
  size: SegmentedControlSize,
  fullWidth: boolean,
  className?: string,
): string {
  return cn(
    segmentedTrackSurfaceClassName,
    "items-center",
    fullWidth ? "flex w-full" : "inline-flex",
    TRACK_SIZE[size],
    className,
  );
}

function segmentClassName(
  context: SegmentedContextValue,
  className?: string,
): string {
  return cn(
    segmentedSegmentBaseClassName,
    segmentedSelectedClassName,
    SEGMENT_SIZE[context.size],
    context.fullWidth && "flex-1",
    className,
  );
}

export interface SegmentedControlProps<
  TValue extends string = string,
> extends Omit<
  React.HTMLAttributes<HTMLDivElement>,
  "onChange" | "defaultValue" | "dir"
> {
  /** The selected option. The control is always controlled. */
  value: TValue;
  /** Called with a different option; re-selecting the current one is ignored. */
  onValueChange: (value: TValue) => void;
  selection?: SegmentedControlSelection;
  size?: SegmentedControlSize;
  /** Stretch the track and give every option an equal share. */
  fullWidth?: boolean;
  disabled?: boolean;
}

/**
 * A state switch between two to four mutually exclusive options that stay
 * visible (Dag · Vecka · Månad, Medarbetare · Kund). Label it with
 * `aria-label` (or `aria-labelledby`). Each option is a
 * `SegmentedControlItem`. For options that are routes, use `SegmentedNav`.
 *
 * @example
 * ```tsx
 * <SegmentedControl aria-label="Tidsspann" value={preset} onValueChange={setPreset}>
 *   <SegmentedControlItem value="dag">Dag</SegmentedControlItem>
 *   <SegmentedControlItem value="vecka">Vecka</SegmentedControlItem>
 * </SegmentedControl>
 * ```
 */
export function SegmentedControl<TValue extends string = string>({
  value,
  onValueChange,
  selection = "radio",
  size = "sm",
  fullWidth = false,
  disabled = false,
  className,
  children,
  ...props
}: SegmentedControlProps<TValue>) {
  const onSelect = (next: string) => {
    if (next !== value) onValueChange(next as TValue);
  };
  const context: SegmentedContextValue = {
    size,
    fullWidth,
    kind: selection,
    value,
    onSelect,
    disabled,
  };
  const track = trackClassName(size, fullWidth, className);

  return (
    <SegmentedContext.Provider value={context}>
      {selection === "pressed" ? (
        <div role="group" className={track} {...props}>
          {children}
        </div>
      ) : (
        <RadioGroupPrimitive.Root
          value={value}
          onValueChange={onSelect}
          orientation="horizontal"
          loop
          disabled={disabled}
          className={track}
          {...props}
        >
          {children}
        </RadioGroupPrimitive.Root>
      )}
    </SegmentedContext.Provider>
  );
}

export interface SegmentedControlItemProps extends Omit<
  React.ButtonHTMLAttributes<HTMLButtonElement>,
  "value" | "type"
> {
  value: string;
}

/** One option of a `SegmentedControl`: a radio, or a pressed toggle button. */
export const SegmentedControlItem = React.forwardRef<
  HTMLButtonElement,
  SegmentedControlItemProps
>(({ value, className, disabled, onClick, ...props }, ref) => {
  const context = useSegmentedContext("SegmentedControlItem");
  const selected = context.value === value;
  const itemClassName = segmentClassName(context, className);

  if (context.kind === "pressed") {
    return (
      <button
        ref={ref}
        type="button"
        aria-pressed={selected}
        data-state={selected ? "on" : "off"}
        data-selected={selected}
        disabled={disabled || context.disabled}
        className={itemClassName}
        onClick={(event) => {
          onClick?.(event);
          if (!event.defaultPrevented) context.onSelect?.(value);
        }}
        {...props}
      />
    );
  }

  return (
    <RadioGroupPrimitive.Item
      ref={ref}
      value={value}
      disabled={disabled}
      data-selected={selected}
      className={itemClassName}
      onClick={onClick}
      {...props}
    />
  );
});
SegmentedControlItem.displayName = "SegmentedControlItem";

export interface SegmentedNavProps extends React.HTMLAttributes<HTMLElement> {
  size?: SegmentedControlSize;
  fullWidth?: boolean;
}

/**
 * The segmented look for options that are routes (Lista · Filter): a labelled
 * `<nav>` whose items are links, the current one marked `aria-current="page"`.
 * Links keep browser navigation, back and open-in-new-tab.
 */
export function SegmentedNav({
  size = "sm",
  fullWidth = false,
  className,
  children,
  ...props
}: SegmentedNavProps) {
  return (
    <SegmentedContext.Provider value={{ size, fullWidth, kind: "nav" }}>
      <nav className={trackClassName(size, fullWidth, className)} {...props}>
        {children}
      </nav>
    </SegmentedContext.Provider>
  );
}

export interface SegmentedNavItemProps extends React.AnchorHTMLAttributes<HTMLAnchorElement> {
  /** The route currently shown: raised, and announced as the current page. */
  active?: boolean;
  /** Render the child (the app's router link) as the segment. */
  asChild?: boolean;
}

/** One route of a `SegmentedNav`; pass the app's `<Link>` with `asChild`. */
export const SegmentedNavItem = React.forwardRef<
  HTMLAnchorElement,
  SegmentedNavItemProps
>(({ active = false, asChild = false, className, ...props }, ref) => {
  const context = useSegmentedContext("SegmentedNavItem");
  const Component = asChild ? Slot : "a";
  return (
    <Component
      ref={ref}
      aria-current={active ? "page" : undefined}
      data-selected={active}
      className={segmentClassName(context, className)}
      {...props}
    />
  );
});
SegmentedNavItem.displayName = "SegmentedNavItem";
